import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
// import { stableStringify } from '../utils/queryUtils';
import service from '../service';
import type { Venue } from '../types/venue'; // <-- whatever your Venue type is

type VenuesResponse = Venue[];
type DefaultError = unknown;

type UseVenuesParams = {
  pageNumber?: number;
  pageSize?: number;
  /**
   * Comma-separated list of relations, e.g. 'spaces,toastRestaurant,toastPrivateDiningMenu,toastCorporateDiningMenu'
   * This matches your `include` query param that `parseInclude` reads.
   */
  include?: string;
  toastInclude?: string;
  /** Override default enabled behavior */
  enabled?: boolean;
  [key: string]: string | number | boolean | undefined | null;
};

/**
 * useVenues
 *
 * @example
 * const venuesQuery = useVenues(
 *   {
 *     pageNumber: 1,
 *     pageSize: 10,
 *     include: 'spaces,toastRestaurant,toastPrivateDiningMenu,toastCorporateDiningMenu',
 *   },
 *   {
 *     staleTime: 60_000,
 *   },
 * );
 */
export function useVenues<TData = VenuesResponse, TError = DefaultError>(
  {
    pageNumber = 1,
    pageSize = 10,
    include,
    toastInclude,
    enabled = true,
    ...restParams
  }: UseVenuesParams = {},
  options?: Omit<
    UseQueryOptions<VenuesResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  // If you really want stableStringify, you could do:
  // const stableParams = useMemo(
  //   () => stableStringify({ pageNumber, pageSize, include }),
  //   [pageNumber, pageSize, include],
  // );

  // TanStack Query can safely hash objects/values in the key,
  // so we can just use a tuple key:
  const queryKey: QueryKey = [
    'venues',
    pageNumber,
    pageSize,
    include,
    restParams,
  ];

  return useQuery<VenuesResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () =>
      service.getVenues({
        pageNumber,
        pageSize,
        ...(include ? { include } : {}),
        ...(toastInclude ? { toastInclude } : {}),
        ...(restParams ? { ...restParams } : {}),
      }),
    ...(options as any),
  });
}
